import type { SurveyScoreScale } from 'definitions';
import type { SurveyDraft } from './useSurvey';
import { SURVEY_SCORE_SCALES, surveyScoreScale } from 'definitions';

/*
 * The question editor's rules, pure so they can be tested without mounting the
 * wizard (useForm writes through the DOM). The limits mirror the API's Zod
 * schema in src/servers/api/surveys/schemas.ts, which rejects anything past them.
 */

export type DraftQuestion = SurveyDraft['questions'][number];
export type QuestionKind = DraftQuestion['kind'];

export const MAX_QUESTIONS = 5;
export const MAX_OPTIONS = 10;
export const MAX_END_LABEL = 60;

export const QUESTION_KIND_LABEL: Record<QuestionKind, string> = {
  score: 'Score',
  'single choice': 'Single choice',
  'multiple choice': 'Multiple choice',
  'open text': 'Text',
};

const isChoice = (kind: QuestionKind) => kind === 'single choice' || kind === 'multiple choice';

const ALL_KINDS: QuestionKind[] = ['score', 'single choice', 'multiple choice', 'open text'];

/**
 * What can be added to a survey of a type. An NPS, CSAT or CES survey has its one
 * score question already; a second would double every count on the results screen,
 * and the API refuses it (questionShapeProblem).
 */
export const addableKinds = (type: SurveyDraft['type']): QuestionKind[] =>
  type === 'Custom' ? ALL_KINDS : ALL_KINDS.filter((kind) => kind !== 'score');

/**
 * Questions in a form where only meaning counts: config keys sorted, and empty
 * strings and absent values dropped. Deciding "did the questions change" on raw
 * JSON made a typed-then-cleared label, or a key moved by an edit, look like a
 * change, and a change on a survey that has run is refused with a 409.
 */
export const canonicalQuestions = (questions: readonly DraftQuestion[]) =>
  questions.map((question) => ({
    kind: question.kind,
    label: question.label,
    required: question.required,
    config: Object.fromEntries(
      Object.entries(question.config ?? {})
        .filter(([, value]) => value !== undefined && value !== null && value !== '')
        .sort(([a], [b]) => a.localeCompare(b)),
    ),
  }));

/** The scale named on a question's config, if it is a real one. */
export const configScale = (question: DraftQuestion): SurveyScoreScale | undefined => {
  const scale = question.config?.scale;
  return SURVEY_SCORE_SCALES.find((candidate) => candidate === scale);
};

/** The scale a score question is drawn on, NPS while a Custom question names none. */
export const scaleFor = (question: DraftQuestion, surveyType: SurveyDraft['type']): SurveyScoreScale =>
  surveyScoreScale({ surveyType, scale: configScale(question) }) ?? 'NPS';

export const optionsOf = (question: DraftQuestion): string[] => {
  const options = question.config?.options;
  return Array.isArray(options) ? options.filter((option): option is string => typeof option === 'string') : [];
};

/** A new question of a kind, filled enough to be valid as soon as it is added. */
export function newQuestion(kind: QuestionKind): DraftQuestion {
  if (kind === 'score') {
    return { kind, label: 'How would you rate your experience?', required: true, config: { scale: 'CSAT' } };
  }
  if (isChoice(kind)) return { kind, label: 'Which of these applies?', required: false, config: { options: ['', ''] } };
  return { kind, label: 'Anything else you would like to add?', required: false, config: {} };
}

/**
 * On an NPS, CSAT or CES survey the first score question IS the instrument: it
 * cannot be removed, moved or made optional, or the survey stops measuring what
 * its type says.
 */
export const isAnchor = (draft: SurveyDraft, index: number): boolean =>
  draft.type !== 'Custom' && draft.questions.findIndex((question) => question.kind === 'score') === index;

/** What is wrong with one question, or null. Shown under the question and on the blocked Continue. */
export function questionProblem(question: DraftQuestion): string | null {
  if (!question.label.trim()) return 'Every question needs a label';
  if (isChoice(question.kind)) {
    const options = optionsOf(question).map((option) => option.trim());
    if (options.length < 2) return 'A choice question needs at least two options';
    if (options.some((option) => !option)) return 'Fill in or remove the empty option';
    if (new Set(options).size !== options.length) return 'Two options say the same thing';
  }
  return null;
}

/** What stops the Questions step, or null. */
export function questionsProblem(draft: SurveyDraft): string | null {
  if (draft.questions.length === 0) return 'Add at least one question';
  if (draft.questions.length > MAX_QUESTIONS) return `At most ${MAX_QUESTIONS} questions`;
  // Without a score there is nothing to compute NPS, CSAT or CES from, and with two
  // every count would double. The same rule as the API's questionShapeProblem.
  if (draft.type !== 'Custom') {
    const scores = draft.questions.filter((question) => question.kind === 'score').length;
    if (scores === 0) return `A ${draft.type} survey needs its score question`;
    if (scores > 1) return `A ${draft.type} survey has only one score question`;
  }
  for (const question of draft.questions) {
    const problem = questionProblem(question);
    if (problem) return problem;
  }
  return null;
}

type MoveParams = { questions: readonly DraftQuestion[]; from: number; to: number };

export function moveQuestion({ questions, from, to }: MoveParams): DraftQuestion[] {
  if (to < 0 || to >= questions.length || from === to) return [...questions];
  const next = [...questions];
  const [moved] = next.splice(from, 1);
  if (moved) next.splice(to, 0, moved);
  return next;
}

/**
 * Options trimmed, and a Custom score question's scale made explicit, as the API
 * requires. Called on save, so a trailing space typed into an option is not two
 * distinct options server-side.
 */
export const questionsForSave = (draft: SurveyDraft): DraftQuestion[] =>
  draft.questions.map((question) => {
    if (isChoice(question.kind)) {
      return {
        ...question,
        config: { ...question.config, options: optionsOf(question).map((option) => option.trim()) },
      };
    }
    if (question.kind === 'score' && draft.type === 'Custom') {
      return { ...question, config: { ...question.config, scale: scaleFor(question, draft.type) } };
    }
    return question;
  });
